export const SPECIAL_NUMBER_BADGE = 'special-number-member';

export type SpecialNumberView = {
  registrationNumber: number;
  categories: string[];
  badgeName: string;
  cardTotal?: number;
  role?: string;
  registrant?: { displayName?: string; channelName?: string };
  directReferrer?: { displayName?: string; channelName?: string };
  recipient?: { displayName?: string; channelName?: string };
  directCardTotal?: number;
  secondLevelCardTotal?: number;
  deliveries?: Array<{
    role?: string;
    cardTotal?: number;
    registrationNumber?: number;
    registrant?: { displayName?: string; channelName?: string };
    directReferrer?: { displayName?: string; channelName?: string };
    directCardTotal?: number;
    secondLevelCardTotal?: number;
  }>;
};

export type SpecialNumberRuleRow = {
  ruleId: string;
  name: string;
  method: string;
  amount: number;
  newMemberAmount: number;
  referrerAmount: number;
  secondLevelAmount: number;
  enabled: boolean;
  revision: number;
  numberCount: number;
  numbers: number[];
};

export type NumberingOutcome = {
  status?: string;
  counter?: number;
  assigned?: number;
  alreadyOpen?: boolean;
  reason?: string;
};

type BadgeRow = {
  code?: string;
  badge?: { code?: string; name?: string };
  meta?: Record<string, unknown>;
};

export function specialNumberFromBadges(badges: unknown): SpecialNumberView | null {
  if (!Array.isArray(badges)) return null;
  const badge = (badges as BadgeRow[]).find((item) => (
    item?.badge?.code === SPECIAL_NUMBER_BADGE || item?.code === SPECIAL_NUMBER_BADGE
  ));
  return specialNumberFromMeta(badge?.meta, badge?.badge?.name);
}

export function specialNumberFromMeta(meta: unknown, badgeName?: string): SpecialNumberView | null {
  if (!meta || typeof meta !== 'object') return null;
  const row = meta as { registeredUserNumber?: unknown; ruleNames?: unknown };
  const registrationNumber = Number(row.registeredUserNumber);
  const categories = Array.isArray(row.ruleNames)
    ? row.ruleNames.filter((name): name is string => typeof name === 'string' && name.length > 0)
    : [];
  if (!Number.isSafeInteger(registrationNumber) || registrationNumber < 1 || categories.length === 0) {
    return null;
  }
  return {
    registrationNumber,
    categories,
    badgeName: badgeName || 'Special Number Member',
  };
}

export function readRecognitionPayload(body: unknown): SpecialNumberView | null {
  const row = unwrapRecord(body, 'registrationNumber');
  if (!row) return null;
  const registrationNumber = Number(row.registrationNumber);
  const categories = Array.isArray(row.categories)
    ? row.categories.filter((name): name is string => typeof name === 'string' && name.length > 0)
    : [];
  const cardTotal = Number(row.cardTotal);
  const role = typeof row.role === 'string' ? row.role : undefined;
  const referral = role === 'referrer' || role === 'second-level';
  if (!Number.isSafeInteger(registrationNumber) || registrationNumber < 1) return null;
  if (!referral && categories.length === 0) return null;
  const deliveries = Array.isArray(row.deliveries)
    ? row.deliveries.filter((item): item is NonNullable<SpecialNumberView['deliveries']>[number] => Boolean(item) && typeof item === 'object')
    : undefined;
  const person = (value: unknown) => {
    if (!value || typeof value !== 'object') return undefined;
    const named = value as { displayName?: unknown; channelName?: unknown };
    return {
      displayName: typeof named.displayName === 'string' ? named.displayName : undefined,
      channelName: typeof named.channelName === 'string' ? named.channelName : undefined,
    };
  };
  const directCardTotal = Number(row.directCardTotal);
  const secondLevelCardTotal = Number(row.secondLevelCardTotal);
  return {
    registrationNumber,
    categories,
    badgeName: typeof row.badgeName === 'string' ? row.badgeName : 'Special Number Member',
    cardTotal: Number.isSafeInteger(cardTotal) ? cardTotal : undefined,
    role,
    registrant: person(row.registrant),
    directReferrer: person(row.directReferrer),
    recipient: person(row.recipient),
    directCardTotal: Number.isSafeInteger(directCardTotal) ? directCardTotal : undefined,
    secondLevelCardTotal: Number.isSafeInteger(secondLevelCardTotal) ? secondLevelCardTotal : undefined,
    deliveries,
  };
}

function categoryListText(names: string[]): string {
  const categoryNames = names.filter((name) => name.length > 0);
  if (categoryNames.length === 0) return 'Special Number';
  if (categoryNames.length === 1) return categoryNames[0];
  if (categoryNames.length === 2) return `${categoryNames[0]} and ${categoryNames[1]}`;
  return `${categoryNames.slice(0, -1).join(', ')} and ${categoryNames[categoryNames.length - 1]}`;
}

function formatCount(value: number): string {
  return value.toLocaleString('en-US');
}

function awardPhrase(value: number | undefined): string | null {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) return null;
  return `${formatCount(value)} Single LA Pass Gift ${value === 1 ? 'Card' : 'Cards'}`;
}

function identity(person?: { displayName?: string; channelName?: string }): string {
  const name = person?.displayName?.trim() || '';
  const channel = person?.channelName?.trim() || '';
  if (name && channel) return `${name} (@${channel})`;
  if (name) return name;
  return channel ? `@${channel}` : '';
}

export function referralDeliveryCopy(delivery: {
  role?: string;
  cardTotal?: number;
  registrationNumber?: number;
  categories?: string[];
  registrant?: { displayName?: string; channelName?: string };
  directReferrer?: { displayName?: string; channelName?: string };
  recipient?: { displayName?: string; channelName?: string };
  directCardTotal?: number;
  secondLevelCardTotal?: number;
}): string {
  const categoryText = categoryListText(delivery.categories || []);
  const number = formatCount(Number(delivery.registrationNumber || 0));
  const recipientName = delivery.recipient?.displayName?.trim() || '';
  const greeting = recipientName ? `Congratulations ${recipientName}` : 'Congratulations';
  const registrant = identity(delivery.registrant);
  const registrantLabel = registrant || 'the registrant';
  if (delivery.role === 'second-level') {
    const ownAward = awardPhrase(delivery.secondLevelCardTotal);
    const directAward = awardPhrase(delivery.directCardTotal);
    const direct = identity(delivery.directReferrer);
    const directLabel = direct || 'the direct referrer';
    const won = ownAward ? `${greeting}, you won ${ownAward}!` : `${greeting}!`;
    const directSentence = directAward
      ? `The direct referrer, ${directLabel}, received ${directAward}.`
      : `The direct referrer is ${directLabel}.`;
    const ownSentence = ownAward ? ` You received ${ownAward}.` : '';
    return `${won}\n\n${directSentence}${ownSentence} Both referrers received their respective Single cards.\n\nThe registrant, ${registrantLabel}, received Special Member Registration Number ${number}, which is a ${categoryText}.`;
  }
  const ownAward = awardPhrase(delivery.cardTotal);
  const won = ownAward ? `${greeting}, you won ${ownAward}!` : `${greeting}!`;
  const referral = registrant
    ? `Your direct referral, ${registrant},`
    : 'Your direct referral';
  return `${won}\n\n${referral} received Special Member Registration Number ${number}, which is a ${categoryText}.`;
}

export function notificationBellPreview(message: string): string {
  return String(message || '').split(/\n\s*\n/)[0].trim();
}

export function registrantAwardCopy(view: SpecialNumberView): string {
  const categoryText = categoryListText(view.categories || []);
  const cards = awardPhrase(view.cardTotal) || awardPhrase(0);
  const registrationNumber = formatCount(Number(view.registrationNumber || 0));
  return `You won ${cards}!!\n\nYour registration number, ${registrationNumber} is a Special Member Registration Number. It is a ${categoryText}!!`;
}

export function numberingMessage(result: NumberingOutcome): { kind: 'message' | 'error'; text: string } {
  if (result.status === 'stopped' || result.status === 'partial') {
    return { kind: 'error', text: result.reason || 'Numbering did not finish' };
  }
  if (result.status !== 'open' || !Number.isSafeInteger(result.counter)) {
    return { kind: 'error', text: 'Numbering did not finish' };
  }
  const highest = String(result.counter);
  if (result.alreadyOpen) {
    return { kind: 'message', text: `Numbering is already open. Highest number is ${highest}.` };
  }
  const count = Number.isSafeInteger(result.assigned) ? result.assigned : 0;
  return { kind: 'message', text: `Numbered ${count} registered members. Highest number is ${highest}.` };
}

export function readSpecialRules(body: unknown): SpecialNumberRuleRow[] {
  let rows: unknown[] = [];
  if (Array.isArray(body)) rows = body;
  else if (body && typeof body === 'object' && Array.isArray((body as { data?: unknown }).data)) {
    rows = (body as { data: unknown[] }).data;
  }
  return rows.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const row = item as Partial<SpecialNumberRuleRow>;
    if (!row.ruleId || !row.name) return [];
    const newMemberAmount = Number(row.newMemberAmount != null ? row.newMemberAmount : row.amount);
    return [{
      ruleId: String(row.ruleId),
      name: String(row.name),
      method: String(row.method || ''),
      amount: newMemberAmount,
      newMemberAmount,
      referrerAmount: Number(row.referrerAmount || 0),
      secondLevelAmount: Number(row.secondLevelAmount || 0),
      enabled: Boolean(row.enabled),
      revision: Number(row.revision || 0),
      numberCount: Number(row.numberCount || 0),
      numbers: Array.isArray(row.numbers)
        ? row.numbers.filter((value): value is number => Number.isSafeInteger(value))
        : [],
    }];
  });
}

function unwrapRecord(body: unknown, marker: string): Record<string, unknown> | null {
  if (!body || typeof body !== 'object') return null;
  const record = body as Record<string, unknown>;
  if (marker in record) return record;
  const wrapped = record.data;
  if (wrapped && typeof wrapped === 'object' && marker in (wrapped as Record<string, unknown>)) {
    return wrapped as Record<string, unknown>;
  }
  return null;
}
